import { useCallback, useEffect, useState } from 'react'
import type { BasketItem, DictionaryIngredient } from '@entities/calculator'
import ProductAmountForm from './ProductAmountForm'

/** Столько длится анимация закрытия в CSS (`cost-sheet-out`). */
const EXIT_MS = 180

interface ProductEditorModalProps {
  ingredient: DictionaryIngredient
  /** Позиция уже в корзине — открываем её значения на правку. */
  item?: BasketItem
  isSaving: boolean
  error: string | null
  /** Внешний запрос на закрытие: например, продукт успешно сохранён. */
  closeRequested: boolean
  onSubmit: (amount: string, price: string) => void
  onClose: () => void
}

/**
 * Шторка с ценой продукта. Держит собственное закрытие: элемент остаётся в
 * дереве, пока доигрывает анимация, иначе форма исчезала бы мгновенно и слой
 * читался бы как сбой отрисовки.
 *
 * Монтируется с `key` по продукту, поэтому состояние анимации не тянется от
 * предыдущей открытой позиции.
 */
const ProductEditorModal = ({
  ingredient,
  item,
  isSaving,
  error,
  closeRequested,
  onSubmit,
  onClose,
}: ProductEditorModalProps) => {
  const [isClosing, setClosing] = useState(false)

  const startClosing = useCallback(() => setClosing(true), [])

  useEffect(() => {
    if (closeRequested) setClosing(true)
  }, [closeRequested])

  useEffect(() => {
    if (!isClosing) return
    const timer = window.setTimeout(onClose, EXIT_MS)
    return () => window.clearTimeout(timer)
  }, [isClosing, onClose])

  return (
    // Клик по фону закрывает форму: в вебе кнопки «Назад» у оверлея нет.
    <div
      className={`cost-modal ${isClosing ? 'closing' : ''}`}
      role="dialog"
      aria-modal="true"
      onClick={event => {
        if (event.target === event.currentTarget) startClosing()
      }}
    >
      <ProductAmountForm
        ingredient={ingredient}
        initialAmount={item?.amount ?? ''}
        initialPrice={item?.price ?? ''}
        isSaving={isSaving}
        error={error}
        onSubmit={onSubmit}
        onCancel={startClosing}
      />
    </div>
  )
}

export default ProductEditorModal
